import {
  BinderResponseDtoStatusEnum,
  CreateBinderPayloadDto,
} from 'libs/pinnacle-sdk';
import * as yup from 'yup';
import { TFunction } from 'i18next';

export const getValidationSchema = (t: TFunction) =>
  yup.object().shape({
    binderName: yup
      .string()
      .trim()
      .required(t('validation.required', { field: t('binder.binderName') })),
    binderLongName: yup
      .string()
      .trim()
      .required(
        t('validation.required', { field: t('binder.binderLongName') })
      ),
    authorityStartDate: yup
      .date()
      .required(
        t('validation.required', { field: t('binder.authorityStartDate') })
      ),
    underwritingYear: yup.string().required(
      t('validation.required', {
        field: t('submission.underwritingYear'),
      })
    ),
    authorityEndDate: yup
      .date()
      .required(
        t('validation.required', { field: t('binder.authorityEndDate') })
      )
      .min(
        yup.ref('authorityStartDate'),
        t('validation.dateAfterInception', {
          field: t('binder.authorityEndDate'),
          date: t('binder.authorityStartDate'),
        })
      ),
    currency: yup
      .string()
      .required(t('validation.required', { field: t('binder.currency') })),
    primaryMaxLimit: yup
      .string()
      .required(
        t('validation.required', { field: t('binder.primaryMaxLimit') })
      )
      .test(
        'is-numeric',
        t('validation.numeric', { field: t('binder.primaryMaxLimit') }),
        (value) => !value || /^\d*\.?\d+$/.test(value)
      )
      .test(
        'is-non-negative',
        t('validation.invalid', { field: t('binder.primaryMaxLimit') }),
        (value) => !value || parseFloat(value) >= 0
      ),
    qsEolMaxLimit: yup
      .string()
      .required(t('validation.required', { field: t('binder.qsEolMaxLimit') }))
      .test(
        'is-numeric',
        t('validation.numeric', { field: t('binder.qsEolMaxLimit') }),
        (value) => !value || /^\d*\.?\d+$/.test(value)
      )
      .test(
        'is-non-negative',
        t('validation.invalid', { field: t('binder.qsEolMaxLimit') }),
        (value) => !value || parseFloat(value) >= 0
      ),
    territories: yup.array().of(yup.string()),
    markets: yup.array().of(yup.string()),
    lineOfBusinesses: yup.array().of(yup.string()),
    excludedPrincipalOccupations: yup.array().of(yup.string()),
    placeBroker: yup
      .string()
      .required(t('validation.required', { field: t('binder.placeBroker') })),
    placementBrokerCommission: yup
      .string()
      .required(
        t('validation.required', {
          field: t('binder.placementBrokerCommission'),
        })
      )
      .test(
        'is-numeric',
        t('validation.numeric', {
          field: t('binder.placementBrokerCommission'),
        }),
        (value) => !value || /^\d*\.?\d+$/.test(value)
      )
      .test(
        'is-non-negative',
        t('validation.invalid', {
          field: t('binder.placementBrokerCommission'),
        }),
        (value) => !value || parseFloat(value) >= 0
      ),
    pinCommission: yup
      .string()
      .required(t('validation.required', { field: t('binder.pinCommission') }))
      .test(
        'is-numeric',
        t('validation.numeric', { field: t('binder.pinCommission') }),
        (value) => !value || /^\d*\.?\d+$/.test(value)
      )
      .test(
        'is-percentage',
        t('validation.invalid', { field: t('binder.pinCommission') }),
        (value) =>
          !value || (parseFloat(value) >= 0 && parseFloat(value) <= 100)
      ),
    security: yup
      .string()
      .required(
        t('validation.required', { field: t('binder.financialCreditRating') })
      ),
    binderGroup: yup
      .string()
      .required(t('validation.required', { field: t('binder.binderGroup') })),
    claimsReferralAmount: yup
      .string()
      .required(
        t('validation.required', { field: t('binder.claimsReferralAmount') })
      )
      .test(
        'is-numeric',
        t('validation.numeric', {
          field: t('binder.placementBrokerCommission'),
        }),
        (value) => !value || /^\d*\.?\d+$/.test(value)
      )
      .test(
        'is-non-negative',
        t('validation.invalid', {
          field: t('binder.placementBrokerCommission'),
        }),
        (value) => !value || parseFloat(value) >= 0
      ),
    defaultFloatBankAccount: yup.string().trim().optional(),
    fileIds: yup.array().of(yup.string()).optional(),
    status: yup
      .string()
      .oneOf(Object.values(BinderResponseDtoStatusEnum))
      .required(t('validation.required', { field: t('binder.status') })),
    comment: yup.string().optional(),
  });

export const initialValues = {
  binderName: undefined,
  binderLongName: undefined,
  underwritingYear: undefined,
  authorityStartDate: null,
  authorityEndDate: null,
  currency: undefined,
  primaryMaxLimit: undefined,
  qsEolMaxLimit: undefined,
  territories: [],
  markets: [],
  lineOfBusinesses: [],
  placeBroker: undefined,
  placementBrokerCommission: undefined,
  pinCommission: undefined,
  security: undefined,
  binderGroup: undefined,
  claimsReferralAmount: undefined,
  defaultFloatBankAccount: undefined,
  fileIds: [],
  comment: undefined,
  status: BinderResponseDtoStatusEnum.Active,
};

export const fields = Object.keys(
  initialValues
) as (keyof CreateBinderPayloadDto)[];
